import { useQueryClient } from '@tanstack/react-query';
import { useGlobalContext } from '../../GlobalContextProvider';
import { useMutationWithErrorHandling } from '../../hooks/UseMutationWithErrorHandling';
import { useQueryWithErrorHandling } from '../../hooks/UseQueryWithErrorHandling';
import { useGet, usePost } from '../HttpClient';

type CreateOfferRequest = {
  offerName: string;
  description: string;
  termDuration: string;
  interestRate: number;
  startDate: Date;
  endDate: Date;
};

type Offers = {
  currentDate: Date;
  offers: Offer[];
};

export type Offer = {
  currencyCode: string;
  description: string;
  endDate: string;
  merchantIds: string[];
  name: string;
  offerId: string;
  offerType: 'Instalment';
  qualification: { maximumAmount: number; minimumAmount: number };
  rate: { value: number };
  riskBands: string[] | null;
  startDate: string;
  status: 'Active' | 'Inactive';
  termDuration: number;
};

const pillarCode = "cl";

function useOfferService() {
  const get = useGet(true, pillarCode);
  const post = usePost(true);
  return {
    getOffers: () =>
      get<Offers>(
        `/api/proxy/cl/offer-service/admin/offer?offerStatus=Active&offerStatus=Inactive&offerStatus=expired`
      ),
    endOffer: (offerId: string, name: string, endDate: string) =>
      post(`/api/proxy/cl/offer-service/offer/update`, {
        offerId: offerId,
        name: name,
        endDate: endDate,
      }),
    createOffer: (createOfferRequest: CreateOfferRequest, minimumAmount: number, maximumAmount: number) =>
      post<any>(`/api/proxy/cl/offer-service/offer`, {
        ...createOfferRequest,
        offerType: 'Instalment',
        minimumAmount: {
          value: minimumAmount,
          currencyCode: 'GBP',
        },
        maximumAmount: {
          value: maximumAmount,
          currencyCode: 'GBP',
        },
      }),
  };
}

export function useOffers() {
  const { tenantId } = useGlobalContext();
  const { getOffers } = useOfferService();
  return useQueryWithErrorHandling({
    queryKey: ['offers', tenantId],
    queryFn: () => getOffers(),
  });
}

export function useRefreshOffers() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['offers'] });
}

export function useEndOffer() {
  const { endOffer } = useOfferService();
  return useMutationWithErrorHandling({
    mutationFn: ({ offerId, name, endDate }: { offerId: string; name: string; endDate: string }) =>
      endOffer(offerId, name, endDate),
  });
}
